import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { CreationStatus } from 'redux/middleware/api/creation-service/types';
import { showError } from 'redux/modules/notification';
import { goToCreate } from 'redux/modules/router';
import { Dispatch } from 'redux/types';
import { getSuggestedClipsPath } from 'utils/routes';
import {
  creationSuggestionsEstimatedCompletedAtSelector,
  creationSuggestionsStatusSelector,
  videoWizardCreationProgressSelector,
} from './selectors';

export interface UseCreationSuggestionsProgressConfig {
  creationId: number;
  videoFileSize: number;
}

export interface UseCreationSuggestionsProgressResult {
  progress: number | undefined;
  estimatedCompletedAt: number | undefined;
  hasNoProgressEstimate: boolean;
}

/**
 * Tracks the progress of creation suggestions for a video.
 *
 * This hook monitors the status and progress of video creation suggestions from
 * the Redux store. It automatically handles side effects:
 * - Shows an error notification and redirects to the create page if the creation fails;
 * - Redirects to the suggested clips page when the creation completes successfully.
 */
export default function useCreationSuggestionsProgress({
  creationId,
  videoFileSize,
}: UseCreationSuggestionsProgressConfig): UseCreationSuggestionsProgressResult {
  const status = useSelector(creationSuggestionsStatusSelector);
  const progress = useSelector(state =>
    videoWizardCreationProgressSelector(state, videoFileSize),
  );
  const estimatedCompletedAt = useSelector(
    creationSuggestionsEstimatedCompletedAtSelector,
  );

  const history = useHistory();
  const dispatch = useDispatch<Dispatch>();

  const hasNoProgressEstimate = estimatedCompletedAt === undefined;

  useEffect(() => {
    if (status === CreationStatus.FAILED) {
      dispatch(showError('There was an error creating your video'));
      dispatch(goToCreate());
    }

    if (status === CreationStatus.COMPLETED && creationId) {
      history.push(getSuggestedClipsPath(creationId));
    }
  }, [creationId, dispatch, history, status]);

  return {
    progress,
    estimatedCompletedAt,
    hasNoProgressEstimate,
  };
}
